const trackedViewElements = new WeakSet();
const publishedViewElements = new WeakSet();
const visibleViewElements = new WeakSet();
const trackedUpsellElements = new WeakSet();
const visibleUpsellElements = new WeakSet();
const publishedUpsellElements = new WeakSet();
const upsellPublishTimers = new WeakMap();
let newsletterSubscriptionPublished = false;

const CUSTOMER_STATE_KEY = 'fabini_tracking_customer_state';
const CUSTOMER_STATE_ANONYMOUS = 'anonymous';
const LOGIN_INTENT_KEY = 'fabini_tracking_login_intent_at';
const LOGIN_INTENT_TTL_MS = 30 * 60 * 1000;
const NEWSLETTER_EMAIL_KEY = 'fabini_tracking_newsletter_email';

const VIEW_SELECTOR = [
  '[data-ee-promo]',
  '[data-ee-item-list]',
  '[data-ee-collection-list]',
  '[data-ee-bundle-view]',
].join(',');

const UPSELL_SELECTOR = '[data-fabini-cross-sell]';

const VIEW_OBSERVER =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              visibleViewElements.delete(entry.target);
              return;
            }

            visibleViewElements.add(entry.target);
            publishViewEvent(entry.target);
          });
        },
        { threshold: 0.35 }
      )
    : null;

const UPSELL_OBSERVER =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              visibleUpsellElements.delete(entry.target);
              return;
            }

            visibleUpsellElements.add(entry.target);
            scheduleUpsellView(entry.target);
          });
        },
        { threshold: 0.35 }
      )
    : null;

function cleanPayload(payload = {}) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

function getBrowserStorage(storageName) {
  try {
    return window[storageName];
  } catch (_error) {
    return null;
  }
}

function readStorage(storageName, key) {
  try {
    return getBrowserStorage(storageName)?.getItem(key) || '';
  } catch (_error) {
    return '';
  }
}

function writeStorage(storageName, key, value) {
  try {
    getBrowserStorage(storageName)?.setItem(key, value);
  } catch (_error) {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

function removeStorage(storageName, key) {
  try {
    getBrowserStorage(storageName)?.removeItem(key);
  } catch (_error) {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

function normalizeEmail(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeId(value) {
  return value === undefined || value === null || value === '' ? undefined : String(value);
}

function getCustomerPayload(overrides = {}) {
  const customer = window.FabiniTrackingCustomer || {};
  const email = normalizeEmail(overrides.email) || normalizeEmail(customer.email);

  return cleanPayload({
    user_id: normalizeId(customer.user_id),
    customer_id: normalizeId(customer.customer_id),
    email,
  });
}

function getCustomerKey(payload = getCustomerPayload()) {
  return payload.customer_id || payload.user_id || payload.email || '';
}

function markLoginIntent() {
  writeStorage('localStorage', LOGIN_INTENT_KEY, String(Date.now()));
}

function hasRecentLoginIntent() {
  const timestamp = Number(readStorage('localStorage', LOGIN_INTENT_KEY));

  if (!timestamp) return false;

  const isRecent = Date.now() - timestamp <= LOGIN_INTENT_TTL_MS;
  if (!isRecent) {
    removeStorage('localStorage', LOGIN_INTENT_KEY);
  }

  return isRecent;
}

function publishLoginIfNeeded() {
  const payload = getCustomerPayload();
  const customerKey = getCustomerKey(payload);

  if (!customerKey) {
    writeStorage('localStorage', CUSTOMER_STATE_KEY, CUSTOMER_STATE_ANONYMOUS);
    return;
  }

  const previousCustomerKey = readStorage('localStorage', CUSTOMER_STATE_KEY);
  const shouldPublish =
    hasRecentLoginIntent() ||
    previousCustomerKey === CUSTOMER_STATE_ANONYMOUS ||
    (previousCustomerKey && previousCustomerKey !== customerKey);
  let wasPublished = true;

  if (shouldPublish) {
    wasPublished = publish('ee_login', payload);
  }

  if (wasPublished) {
    writeStorage('localStorage', CUSTOMER_STATE_KEY, customerKey);
    removeStorage('localStorage', LOGIN_INTENT_KEY);
  }
}

function isLoginTrigger(element) {
  if (getCustomerKey(getCustomerPayload())) return false;
  if (element.closest?.('.account-actions__sign-ins')) return true;
  if (element.matches?.('shop-login-button')) return true;

  if (element instanceof HTMLAnchorElement) {
    const href = element.getAttribute('href') || '';
    return /\/account\/login|\/account\/register|accounts\.shopify\.com|shopify\.com\/.*\/auth/i.test(href);
  }

  return false;
}

function isNewsletterForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (form.matches('.fabini-newsletter__form, .email-signup__form')) return true;
  if (form.closest('.fabini-newsletter, .email-signup-block')) return true;

  const tagsInput = form.querySelector('input[name="contact[tags]"]');
  return Boolean(tagsInput?.value && /newsletter/i.test(tagsInput.value));
}

function getNewsletterEmailFromForm(form) {
  return normalizeEmail(form.querySelector('input[name="contact[email]"], input[type="email"]')?.value);
}

function rememberNewsletterEmail(form) {
  const email = getNewsletterEmailFromForm(form);
  if (!email) return;

  writeStorage('sessionStorage', NEWSLETTER_EMAIL_KEY, email);
  window.FabiniNewsletterSubscriptionEmail = email;
}

function getStoredNewsletterEmail() {
  return normalizeEmail(readStorage('sessionStorage', NEWSLETTER_EMAIL_KEY));
}

function clearStoredNewsletterEmail() {
  removeStorage('sessionStorage', NEWSLETTER_EMAIL_KEY);
}

function hasNewsletterSuccessSignal() {
  if (window.FabiniNewsletterSubscriptionSuccess) return true;

  if (
    document.querySelector(
      '.fabini-newsletter__success-flag[data-newsletter-success="true"], .email-signup__message--success'
    )
  ) {
    return true;
  }

  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('customer_posted') === 'true' && Boolean(getStoredNewsletterEmail());
}

function publishNewsletterSubscription(email) {
  if (newsletterSubscriptionPublished) return;

  const resolvedEmail =
    normalizeEmail(email) || normalizeEmail(window.FabiniNewsletterSubscriptionEmail) || getStoredNewsletterEmail();
  const payload = cleanPayload({ email: resolvedEmail });

  if (!payload.email) return;

  if (publish('ee_newsletter_subscription', payload)) {
    newsletterSubscriptionPublished = true;
    clearStoredNewsletterEmail();
  }
}

function publishNewsletterSubscriptionIfNeeded() {
  if (!hasNewsletterSuccessSignal()) return;
  publishNewsletterSubscription();
}

function normalizePath(value) {
  if (!value) return '';

  try {
    return new URL(value, window.location.origin).pathname;
  } catch (_error) {
    return value;
  }
}

function publish(name, payload = {}) {
  const eventPayload = cleanPayload(payload);

  if (window.Shopify?.analytics?.publish) {
    window.Shopify.analytics.publish(name, eventPayload);
    return true;
  }

  return false;
}

function getPromoPayload(element) {
  return cleanPayload({
    creative_name: element.dataset.eeCreativeName,
    creative_slot: element.dataset.eeCreativeSlot,
    promotion_name: element.dataset.eePromotionName,
  });
}

function isViewElementHidden(element) {
  return element.closest('[aria-hidden="true"]') !== null;
}

function collectItems(root) {
  const items = [];
  const seen = new Set();

  root.querySelectorAll('[data-ee-item-name]').forEach((itemElement) => {
    const item = cleanPayload({
      item_name: itemElement.dataset.eeItemName,
      item_id: itemElement.dataset.eeItemId,
    });

    if (!item.item_name && !item.item_id) return;

    const key = `${item.item_id || ''}:${item.item_name || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  });

  return items;
}

/**
 * Items of the cross-sell cards currently shown (cards of other shuffle seeds are hidden),
 * in display order: cart offers are ordered per seed with CSS `order`, not in the DOM.
 */
function collectUpsellItems(element) {
  return Array.from(element.querySelectorAll('[data-fabini-cross-sell-item]'))
    .filter((card) => card instanceof HTMLElement && card.getClientRects().length > 0)
    .map((card) => ({ card, order: Number(getComputedStyle(card).order) || 0 }))
    .sort((a, b) => a.order - b.order)
    .map(({ card }) =>
      cleanPayload({
        item_name: card.dataset.eeItemName,
        item_id: card.dataset.eeItemId,
      })
    )
    .filter((item) => item.item_name || item.item_id);
}

function isUpsellVisible(element) {
  return UPSELL_OBSERVER ? visibleUpsellElements.has(element) : true;
}

function scheduleUpsellView(element) {
  if (!(element instanceof HTMLElement) || publishedUpsellElements.has(element) || !isUpsellVisible(element)) return;

  window.clearTimeout(upsellPublishTimers.get(element));
  const timer = window.setTimeout(() => {
    const items = collectUpsellItems(element);
    if (publishedUpsellElements.has(element) || !isUpsellVisible(element) || items.length === 0) return;

    publishedUpsellElements.add(element);
    UPSELL_OBSERVER?.unobserve(element);
    publish('ee_upsell_view', { item_list_name: element.dataset.fabiniCrossSell, items });
  }, 250);

  upsellPublishTimers.set(element, timer);
}

function publishViewEvent(element) {
  if (
    !(element instanceof HTMLElement) ||
    publishedViewElements.has(element) ||
    isViewElementHidden(element)
  ) {
    return;
  }

  publishedViewElements.add(element);
  VIEW_OBSERVER?.unobserve(element);

  if (element.matches('[data-ee-promo]')) {
    publish('ee_view_promotion', getPromoPayload(element));
    return;
  }

  if (element.matches('[data-ee-item-list]')) {
    publish('ee_view_item_list', {
      item_list_name: element.dataset.eeItemListName,
      items: collectItems(element),
    });
    return;
  }

  if (element.matches('[data-ee-collection-list]')) {
    publish('ee_view_collection_list', {
      collection_name: element.dataset.eeCollectionName,
    });
    return;
  }

  if (element.matches('[data-ee-bundle-view]')) {
    publish('ee_bundle_view');
    return;
  }
}

function trackViewElement(element) {
  if (!(element instanceof HTMLElement) || trackedViewElements.has(element)) return;
  trackedViewElements.add(element);

  if (VIEW_OBSERVER) {
    VIEW_OBSERVER.observe(element);
  } else {
    publishViewEvent(element);
  }
}

function trackViewElements(root = document) {
  if (root instanceof Element && root.matches(VIEW_SELECTOR)) {
    trackViewElement(root);
  }

  root.querySelectorAll?.(VIEW_SELECTOR).forEach(trackViewElement);
}

function publishRevealedViewElements(root) {
  if (!(root instanceof Element)) return;

  if (root.matches(VIEW_SELECTOR)) {
    publishViewEvent(root);
  }

  root.querySelectorAll?.(VIEW_SELECTOR).forEach(publishViewEvent);
}

function trackUpsellElement(element) {
  if (!(element instanceof HTMLElement) || trackedUpsellElements.has(element)) return;
  trackedUpsellElements.add(element);

  if (UPSELL_OBSERVER) {
    UPSELL_OBSERVER.observe(element);
  } else {
    visibleUpsellElements.add(element);
  }

  new MutationObserver(() => {
    scheduleUpsellView(element);
  }).observe(element, { childList: true, subtree: true });

  scheduleUpsellView(element);
}

function trackUpsellElements(root = document) {
  if (root instanceof Element && root.matches(UPSELL_SELECTOR)) {
    trackUpsellElement(root);
  }

  root.querySelectorAll?.(UPSELL_SELECTOR).forEach(trackUpsellElement);
}

function handleClick(event) {
  if (!(event.target instanceof Element)) return;

  const loginTrigger = event.target.closest('shop-login-button, a[href]');
  if (loginTrigger instanceof HTMLElement && isLoginTrigger(loginTrigger)) {
    markLoginIntent();
  }

  const promo = event.target.closest('[data-ee-promo]');
  const promoTrigger = event.target.closest('a, button');
  if (promo instanceof HTMLElement && promoTrigger instanceof HTMLElement && promo.contains(promoTrigger)) {
    publish('ee_select_promotion', getPromoPayload(promo));
  }

  const menuItem = event.target.closest('[data-ee-menu-click]');
  if (menuItem instanceof HTMLElement) {
    publish('ee_menu_click', {
      menu_name: menuItem.dataset.eeMenuName,
      menu_text: menuItem.dataset.eeMenuText,
      link_url: normalizePath(menuItem.dataset.eeLinkUrl || menuItem.getAttribute('href')),
      menu_level: Number(menuItem.dataset.eeMenuLevel || 1),
    });
  }

  const collectionItem = event.target.closest('[data-ee-collection-click]');
  if (collectionItem instanceof HTMLElement) {
    const list = collectionItem.closest('[data-ee-collection-list]');
    publish('ee_click_collection_list', {
      collection_name: collectionItem.dataset.eeCollectionName || list?.dataset.eeCollectionName,
      collection_text: collectionItem.dataset.eeCollectionText,
      link_url: normalizePath(collectionItem.dataset.eeLinkUrl || collectionItem.getAttribute('href')),
    });
  }

  const bundleClick = event.target.closest('[data-ee-bundle-click]');
  if (bundleClick instanceof HTMLElement) {
    publish('ee_bundle_click');
  }

  // Cross-sell cards in the cart drawer reuse the theme product card, whose links carry no
  // data-ee attributes; the card element holds the item data instead.
  const selectItem =
    event.target.closest('[data-ee-select-item]') ??
    event.target.closest('[data-fabini-cross-sell-item] a[href*="/products/"]')?.closest('[data-fabini-cross-sell-item]');
  if (selectItem instanceof HTMLElement) {
    const crossSell = selectItem.closest('[data-fabini-cross-sell]');
    publish('ee_select_item', {
      item_list_name: crossSell instanceof HTMLElement ? crossSell.dataset.fabiniCrossSell : undefined,
      items: [
        cleanPayload({
          item_name: selectItem.dataset.eeItemName,
          item_id: selectItem.dataset.eeItemId,
        }),
      ],
    });
  }

  const bundleDetail = event.target.closest('[data-ee-bundle-detail]');
  if (bundleDetail instanceof HTMLElement && bundleDetail.getAttribute('aria-expanded') !== 'true') {
    publish('ee_bundle_detail');
  }
}

function handleSubmit(event) {
  if (!(event.target instanceof HTMLFormElement) || !isNewsletterForm(event.target)) return;
  rememberNewsletterEmail(event.target);
}

function init() {
  publishLoginIfNeeded();
  publishNewsletterSubscriptionIfNeeded();
  trackViewElements();
  trackUpsellElements();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element && mutation.target.getAttribute('aria-hidden') !== 'true') {
          publishRevealedViewElements(mutation.target);
        }
        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          trackViewElements(node);
          trackUpsellElements(node);
        }
      });
    });
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });
}

window.FabiniCustomEvents = {
  publish,
  markLoginIntent,
  publishNewsletterSubscription,
  publishMenuView(menuName) {
    if (!menuName) return;
    publish('ee_menu_view', { menu_name: menuName });
  },
};

// Adds from a cross-sell (its own forms or the quick add in the cart drawer) dispatch the theme's
// cart update event inside the widget. Capture runs before the cart re-render removes the card.
document.addEventListener(
  'cart:update',
  (event) => {
    if (!(event.target instanceof Element) || event.detail?.data?.didError) return;

    const widget = event.target.closest('[data-fabini-cross-sell]');
    const card = event.target.closest('[data-fabini-cross-sell-item]');
    if (!(widget instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

    const item = cleanPayload({ item_name: card.dataset.eeItemName, item_id: card.dataset.eeItemId });
    if (!item.item_name && !item.item_id) return;

    publish('ee_upsell_add', { item_list_name: widget.dataset.fabiniCrossSell, items: [item] });
  },
  { capture: true }
);

window.addEventListener('fabini:newsletter-subscription-success', (event) => {
  publishNewsletterSubscription(event.detail?.email);
});

document.addEventListener('click', handleClick, { capture: true });
document.addEventListener('submit', handleSubmit, { capture: true });

if (!getCustomerKey(getCustomerPayload())) {
  writeStorage('localStorage', CUSTOMER_STATE_KEY, CUSTOMER_STATE_ANONYMOUS);
}

function scheduleInit() {
  const run = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(init, { timeout: 1500 });
      return;
    }

    window.setTimeout(init, 0);
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
}

scheduleInit();
